// DR-017 tenant MFA policy panel — Business plane (Admin / IT-Integration ONLY).
//
// This is TENANT policy governance: the tenant's own Admin/IT configure enforcement mode, targeted
// roles, grace/assurance durations and recovery requirement for THEIR tenant, and may reset a staff
// member's factor. It is NOT the SuperAdmin commercial-entitlement view and NOT personal enrolment.
// It never shows a TOTP secret, otpauth URI, QR, recovery code, assurance token or raw tenant/staff id.
// Configuration is available only when the SuperAdmin-owned MFA capability is entitled for the tenant.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFreshIdToken } from '../../../lib/firebase';
import { toDateInputValue } from '../../../lib/dateInput';
import MfaStaffResetPicker from './MfaStaffResetPicker';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

import { tenantHeaders } from '../../../lib/tenantSession';
import { useBusiness } from '../../../contexts/BusinessContext';

const base = () => process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';
async function api(path, method = 'GET', body) {
  const token = await getFreshIdToken();
  const res = await fetch(`${base()}/api/v1${path}`, {
    method,
    // Tenant-governed policy with no path tenant — the active tenant must travel on the header.
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data = null; try { data = await res.json(); } catch { data = {}; }
  return { ok: res.ok, status: res.status, data };
}

// Roles a tenant policy may target (mirrors backend _TARGETABLE_ROLES; customer/owner/superadmin excluded).
const TARGETABLE = ['operator', 'manager', 'analyst', 'director', 'executive', 'it', 'admin'];
const DEFAULT_POLICY = { status: 'disabled', targeted_roles: [], approved_methods: ['totp'],
  enrolment_grace_days: 14, assurance_minutes: 720, step_up_assurance_minutes: 10, recovery_required: true,
  noncompliance_behaviour: 'restrict' };

const norm = (p) => JSON.stringify({
  status: p?.status, targeted_roles: [...(p?.targeted_roles || [])].sort(), effective_date: toDateInputValue(p?.effective_date),
  enrolment_grace_days: Number(p?.enrolment_grace_days) || 0, assurance_minutes: Number(p?.assurance_minutes) || 720,
  step_up_assurance_minutes: Number(p?.step_up_assurance_minutes) || 10, recovery_required: p?.recovery_required !== false,
});

export default function BusinessMfaPolicyPanel() {
  const { currentBusiness } = useBusiness();
  const tenant = currentBusiness?.brandId || '';
  const tenantRef = useRef(tenant);        // read inside async work to detect a switch
  const [capability, setCapability] = useState(null);   // MFA_CAPABILITY_AVAILABLE | *_NOT_ENTITLED
  const [policy, setPolicy] = useState(null);
  const [serverPolicy, setServerPolicy] = useState(null);   // last server snapshot (for Reset + dirty)
  const [effectiveStatus, setEffectiveStatus] = useState('');
  const [pendingReview, setPendingReview] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // MFA policy belongs to ONE tenant, so every piece of state below is that tenant's - including
  // the error. Previously `load` was useCallback(..., []) with useEffect(..., [load]), so it ran
  // exactly once per mount and never again: switching business kept the previous tenant's policy
  // AND its error on screen, and a failure was permanent because nothing ever refetched. It also
  // fired before the active tenant existed, which is unanswerable for a multi-membership person
  // and returned 409 - the error that then stuck.
  const load = useCallback(async (forTenant) => {
    if (!forTenant) return;                 // nothing to ask about yet; the effect re-runs on arrival
    setLoading(true); setError('');
    const r = await api('/mfa/policy');
    // Stale-response guard: the active tenant may have changed while this was in flight. A late
    // reply from the PREVIOUS tenant must never paint over the current one.
    if (tenantRef.current !== forTenant) return;
    if (!r.ok) { setError(r.status === 403 ? 'You are not authorized to manage MFA policy.' : 'Could not load MFA policy.'); setLoading(false); return; }
    setCapability(r.data.capability);
    const pol = r.data.policy || { ...DEFAULT_POLICY };
    setPolicy(pol); setServerPolicy(pol);
    setEffectiveStatus(r.data.effective_policy_status || '');
    setPendingReview(!!r.data.pending_review);
    setCompliance(r.data.compliance || null);
    setLoading(false);
  }, []);

  // Tenant is part of the identity of this data. Changing it clears the previous tenant's policy
  // and error FIRST, so nothing from the old business can survive the switch even for one paint.
  useEffect(() => {
    tenantRef.current = tenant;
    setError(''); setCapability(null); setPolicy(null); setServerPolicy(null);
    setEffectiveStatus(''); setPendingReview(false); setCompliance(null);
    setLoading(!!tenant);
    load(tenant);
  }, [tenant, load]);

  const dirty = policy && serverPolicy && norm(policy) !== norm(serverPolicy);
  const resetChanges = () => { setPolicy(serverPolicy); setError(''); };
  const upd = (patch) => setPolicy((p) => ({ ...(p || DEFAULT_POLICY), ...patch }));
  const toggleRole = (role) => {
    const cur = new Set(policy?.targeted_roles || []);
    cur.has(role) ? cur.delete(role) : cur.add(role);
    upd({ targeted_roles: Array.from(cur) });
  };

  const save = async () => {
    if (busy) return; setBusy(true); setError(''); setSaved(false);
    const payload = {
      status: policy.status, targeted_roles: policy.targeted_roles || [], approved_methods: ['totp'],
      effective_date: policy.effective_date || null, enrolment_grace_days: Number(policy.enrolment_grace_days) || 0,
      assurance_minutes: Number(policy.assurance_minutes) || 720,
      step_up_assurance_minutes: Number(policy.step_up_assurance_minutes) || 10,
      recovery_required: policy.recovery_required !== false, noncompliance_behaviour: policy.noncompliance_behaviour || 'restrict',
    };
    const r = await api('/mfa/policy', 'PUT', payload);
    setBusy(false);
    if (!r.ok) { setError(r.data?.detail?.code === 'MFA_CAPABILITY_NOT_ENTITLED' ? 'MFA is not enabled for this tenant.' : 'Save failed.'); return; }
    const pol = r.data.policy || policy;
    setSaved(true); setPolicy(pol); setServerPolicy(pol); setPendingReview(false); setEffectiveStatus(pol.status);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) return <Card><CardContent className="py-6 text-sm text-gray-500">Loading MFA policy…</CardContent></Card>;

  return (
    <Card>
      <CardHeader><CardTitle>Multi-Factor Authentication (staff)</CardTitle></CardHeader>
      <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
        {capability !== 'MFA_CAPABILITY_AVAILABLE' ? (
          <p className="text-sm text-gray-600">
            Multi-factor authentication is not enabled for this tenant. A Servisfy SuperAdmin can grant it from Tenant 360 → Features.
          </p>
        ) : (
          <>
            {pendingReview && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                MFA was just enabled for your organization. A previous <b>Required</b> policy is on hold and is
                <b> not enforced</b> until you review and save it below — so no staff are locked out unexpectedly.
              </div>
            )}
            <label className="block text-sm font-medium">Enforcement
              <select value={policy.status} onChange={(e) => upd({ status: e.target.value })}
                className="mt-1 border rounded-md px-3 py-2 w-full">
                <option value="disabled">Disabled</option>
                <option value="optional">Optional</option>
                <option value="required">Required</option>
              </select>
            </label>

            <div>
              <div className="text-sm font-medium mb-1">Targeted staff roles</div>
              <div className="grid grid-cols-2 gap-1">
                {TARGETABLE.map((role) => (
                  <label key={role} className="flex items-center gap-2 text-sm capitalize">
                    <input type="checkbox" checked={(policy.targeted_roles || []).includes(role)} onChange={() => toggleRole(role)} />
                    {role === 'it' ? 'IT/Integration' : role}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">Enrolment grace (days)
                <input type="number" min="0" value={policy.enrolment_grace_days ?? 14}
                  onChange={(e) => upd({ enrolment_grace_days: e.target.value })} className="mt-1 border rounded-md px-2 py-1 w-full" /></label>
              <label className="text-sm">Effective date (optional)
                <input type="date" value={toDateInputValue(policy.effective_date)}
                  onChange={(e) => upd({ effective_date: e.target.value || null })} className="mt-1 border rounded-md px-2 py-1 w-full" /></label>
              <label className="text-sm">Assurance duration (minutes)
                <input type="number" min="1" value={policy.assurance_minutes ?? 720}
                  onChange={(e) => upd({ assurance_minutes: e.target.value })} className="mt-1 border rounded-md px-2 py-1 w-full" /></label>
              <label className="text-sm">Step-up duration (minutes)
                <input type="number" min="1" value={policy.step_up_assurance_minutes ?? 10}
                  onChange={(e) => upd({ step_up_assurance_minutes: e.target.value })} className="mt-1 border rounded-md px-2 py-1 w-full" /></label>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={policy.recovery_required !== false}
                onChange={(e) => upd({ recovery_required: e.target.checked })} /> Require recovery codes
            </label>

            {compliance && (
              <div className="text-sm text-gray-600 border rounded-md p-3">
                Enrolled factors: <b>{compliance.enrolled_factors}</b> of <b>{compliance.active_staff}</b> active staff.
              </div>
            )}

            {/* Card-owned actions — the global Settings Save/Reset do NOT own MFA policy state. */}
            <div className="flex gap-2">
              <button disabled={busy || !dirty} onClick={save}
                className="flex-1 bg-blue-600 text-white rounded-md py-2 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save MFA policy'}</button>
              <button disabled={busy || !dirty} onClick={resetChanges}
                className="rounded-md py-2 px-4 border disabled:opacity-50">Reset changes</button>
            </div>
            {effectiveStatus && !pendingReview && (
              <p className="text-xs text-gray-400">Effective enforcement: <b>{String(effectiveStatus).replace(/_/g, ' ')}</b>.</p>
            )}

            <MfaStaffResetPicker />

            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
              <b>Saving stores the policy only — it does not set up anyone’s authenticator.</b> Each targeted
              staff member sets up MFA privately the next time they sign in to <i>their own</i> account: they
              scan their own QR code, verify their own 6-digit code and get their own recovery codes. You (as
              Admin/IT) never see another person’s QR code, secret or recovery codes.
            </div>
          </>
        )}
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        {saved && <p className="text-sm text-green-600">Saved.</p>}
      </CardContent>
    </Card>
  );
}
